import type { BrandStyle } from '~/design-system/components/DeckGen/types/presentation'

// Shared Birtvy web-card type + geometry + brand-tile builder — the product's WebCard (types/presentation.ts
// + store/canvasItemActions.ts) with the presentation store swapped for props and NUMBER ids (idRef counter)
// instead of the product's string ids (per "duplicate, don't mutate"). One type covers every web-card case:
//   • a normal website  → screenshotUrl (or a live iframe when `frameable`)
//   • an X / Instagram post → `post` set (rendered, not screenshotted — those sites block framing + shots)
//   • a brand-style tile → kind 'swatch' | 'fonts' | 'logo' (a finished artefact, no fetch)

/** Default website card — the product's WEB_CARD_DEFAULT_SIZE. */
export const WEB_CARD_DEFAULT_SIZE = { width: 760, height: 540 } as const
/** A social post is laid out like a post embed (portrait), not a wide screenshot frame — the product's SOCIAL_CARD_SIZE. */
export const SOCIAL_CARD_SIZE = { width: 460, height: 600 } as const
/** Brand-style tile sizes + row gap — the product's SWATCH/FONTS/LOGO_TILE_SIZE + BRAND_TILE_GAP. */
export const SWATCH_TILE_SIZE = { width: 96, height: 104 } as const
export const FONTS_TILE_SIZE = { width: 232, height: 104 } as const
export const LOGO_TILE_SIZE = { width: 132, height: 104 } as const
export const BRAND_TILE_GAP = 14

/** Clearance around ANYTHING the extract capabilities drop: the offset from the source card, and the empty
 *  margin the free-space search reserves on all four sides. The search's clearance test (canvasPlacement
 *  `isClear`) is a strict overlap, so an un-inflated block is allowed to land flush against its neighbour —
 *  inflate by this, place inside the inflated rect, and the block is SPACED, not merely non-overlapping. */
export const WEB_EXTRACT_GAP = 40
/** Gutter between two extracted images inside the block. */
export const WEB_EXTRACT_IMAGE_GAP = 24

/** Social-body auto-resize clamp — the product's SOCIAL_BODY_MIN/MAX_HEIGHT (CanvasWebCards.tsx). */
export const SOCIAL_BODY_MIN_HEIGHT = 140
export const SOCIAL_BODY_MAX_HEIGHT = 1600

/**
 * NOT GROUPABLE, on purpose. Every other item kind carries `groupId` and appears in birtvyGroup's
 * GroupMemberKind; a web card does not. A website and its brand tiles are a REFERENCE surface laid over
 * the work, not part of a deck's structure — and admitting web into GroupMemberKind would drag the
 * mixed-group grid, the reorder packer, the deck-row layout and collapse-hiding along with it. If that
 * ever changes it is its own project; do not add `groupId` here to "finish" the kind.
 */
export interface BirtvyWeb {
  id: number
  x: number
  y: number
  width: number
  height: number
  /** 'web' = a website/social card; the others are brand-style tiles extracted from a page. */
  kind?: 'web' | 'swatch' | 'fonts' | 'logo'
  swatch?: { color: string; label?: string }
  fonts?: { heading?: string; body?: string }
  logo?: { src: string }
  url: string
  title?: string
  /** Full-page screenshot (normal website). Absent for social posts + brand tiles. */
  screenshotUrl?: string
  /** The site allows iframing → render it LIVE instead of the screenshot. */
  frameable?: boolean
  status: 'empty' | 'loading' | 'ready' | 'error'
  /** Candidate image URLs the scrape found — the source for "Extract images". */
  sourceImages?: string[]
  /** A social post (X / Instagram) the backend extracted instead of a screenshot. Set ⇒ render a SocialBody. */
  post?: {
    platform: string
    author?: string
    authorAvatar?: string
    text: string
    images: string[]
    videos?: { url: string; poster?: string; width?: number; height?: number }[]
  }
  /** Brand style extracted from the page CSS (best-effort), stashed for generation + "Extract styles". */
  brandStyle?: BrandStyle
  /** When the preview/extract last fetched successfully (epoch ms). Set by ingest + re-fetch; absent on
   *  cards saved before it existed, and a display that reads it must say nothing rather than guess. */
  checkedAt?: number
  /** A user-given name for the card. Absent = display falls back to the page title / hostname. */
  label?: string
}

/** Sites that block BOTH iframing and screenshotting — render the POST itself instead (the product's
 *  SOCIAL_POST_HOSTS in useWebCardIngest.ts). */
const SOCIAL_POST_HOSTS = /(^|\.)(x\.com|twitter\.com|instagram\.com)$/i
export function isSocialPostUrl(url: string): boolean {
  try {
    return SOCIAL_POST_HOSTS.test(new URL(url).hostname)
  } catch {
    return false
  }
}

// "Extract text" on a NON-social site hits the same backend extractor, which falls through to a Firecrawl
// markdown scrape of the page. `onlyMainContent` isolates an ARTICLE body — a marketing/landing page has
// none, so the markdown keeps the nav row, the hero CTA, the footer link list and the icon images. The note
// parses that markdown for real (RICH_MARKDOWN), so each of those lands as an underlined anchor or an inline
// <img>. Strip the chrome before the text reaches a note. Links INSIDE a sentence are kept — in a real
// article they are part of the prose.
const MD_IMAGE_G = /!\[[^\]]*\]\([^)]*\)/g
const MD_LINK_G = /\[((?:[^[\]]|\[[^\]]*\])*)\]\([^)]*\)/g
const HEADING_MARKER = /^\s*#{1,6}\s+/
const LIST_MARKER = /^\s*(?:[-*+]|\d+[.)])\s+/
const wordsOnly = (s: string): string => s.replace(/[^\p{L}\p{N}]+/gu, '')

/** Scraped page markdown → note-worthy prose: drop icon/badge images and every line that is nothing but
 *  short links (a nav row, a CTA button, a footer link list), then collapse the blank runs those drops
 *  leave behind. A LONG link-only line survives — a linked article headline is content, not chrome. */
export function readableFromScrapedMarkdown(markdown: string): string {
  const kept: string[] = []
  for (const raw of markdown.split('\n')) {
    const line = raw.replace(MD_IMAGE_G, '').replace(/\s+$/, '')
    const body = line.replace(HEADING_MARKER, '').replace(LIST_MARKER, '')
    const labels = body.replace(MD_LINK_G, '$1').trim() // link labels inlined
    const bare = body.replace(MD_LINK_G, '').trim()     // link labels removed
    if (!wordsOnly(labels)) { kept.push(''); continue }                      // blank / image-only / `[](#anchor)`
    if (!wordsOnly(bare) && labels.length <= 80) { kept.push(''); continue } // the line IS links → nav / CTA
    kept.push(line)
  }
  return kept.join('\n').replace(/\n{3,}/g, '\n\n').trim()
}

/** A readable hostname for a card header (falls back to the raw URL) — the product's hostnameOf. */
export function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

/** Route a social-CDN image through our same-origin image proxy (avatars / carousel photos / video posters).
 *  IG/X CDNs serve a server fine but block a browser <img>'s cross-origin context — the product's proxiedImg. */
export function proxiedImg(url: string): string {
  return `/deckgen-image-proxy-api?url=${encodeURIComponent(url)}`
}

/** X's mp4s hotlink-block a cross-origin Referer (403) — stream them through our same-origin proxy. */
export function socialVideoSrc(url: string): string {
  return `/social-video-proxy-api?url=${encodeURIComponent(url)}`
}

/** Build the row of brand-style tiles below a web card — a straight port of the product's addBrandStyleTiles
 *  (store/canvasItemActions.ts): one swatch per colour, a fonts tile, then a logo tile, laid left-to-right at
 *  `origin`. `mkId` mints Birtvy's numeric ids (idRef). Returns [] when the style carries nothing to show. */
export function buildBrandStyleTiles(
  origin: { x: number; y: number },
  style: BrandStyle,
  mkId: () => number,
): BirtvyWeb[] {
  const palette = (style.palette ?? []).slice(0, 8)
  const roleLabels: Record<string, string> = {
    accent: 'Accent', background: 'Background', surface: 'Surface',
    text: 'Text', textMuted: 'Muted', border: 'Border',
  }
  const roleByHex = new Map<string, string>()
  for (const [role, hex] of Object.entries(style.roles ?? {})) {
    if (typeof hex === 'string') roleByHex.set(hex.toLowerCase(), roleLabels[role] ?? role)
  }
  const tiles: BirtvyWeb[] = []
  let x = Math.round(origin.x)
  const y = Math.round(origin.y)
  for (const color of palette) {
    tiles.push({
      id: mkId(), x, y,
      width: SWATCH_TILE_SIZE.width, height: SWATCH_TILE_SIZE.height,
      kind: 'swatch', url: '', status: 'ready',
      swatch: { color, label: roleByHex.get(color.toLowerCase()) },
    })
    x += SWATCH_TILE_SIZE.width + BRAND_TILE_GAP
  }
  if (style.headingFont || style.bodyFont) {
    tiles.push({
      id: mkId(), x, y,
      width: FONTS_TILE_SIZE.width, height: FONTS_TILE_SIZE.height,
      kind: 'fonts', url: '', status: 'ready',
      fonts: { heading: style.headingFont, body: style.bodyFont },
    })
    x += FONTS_TILE_SIZE.width + BRAND_TILE_GAP
  }
  if (style.logoUrl) {
    tiles.push({
      id: mkId(), x, y,
      width: LOGO_TILE_SIZE.width, height: LOGO_TILE_SIZE.height,
      kind: 'logo', url: '', status: 'ready',
      logo: { src: style.logoUrl },
    })
  }
  return tiles
}
